import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Search, X, Building2, MapPin, BedDouble, Bath, Car, Edit2, Trash2,
} from 'lucide-react';

const formatCurrency = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v || 0);

const TIPOS = ['', 'apartamento', 'casa', 'terreno', 'comercial', 'cobertura'];
const STATUS_LIST = ['', 'disponivel', 'em_negociacao', 'vendido', 'alugado'];
const TIPO_LABELS = { apartamento: 'Apartamento', casa: 'Casa', terreno: 'Terreno', comercial: 'Comercial', cobertura: 'Cobertura' };
const STATUS_LABELS = { disponivel: 'Disponível', em_negociacao: 'Em negociação', vendido: 'Vendido', alugado: 'Alugado' };
const STATUS_COLORS = { disponivel: 'badge-disponivel', em_negociacao: 'badge-negociacao', vendido: 'badge-vendido', alugado: 'badge-alugado' };

const EMPTY_FORM = {
  titulo: '', tipo: 'apartamento', status: 'disponivel', valor: '',
  valor_condominio: '', area: '', quartos: '', banheiros: '', vagas: '',
  endereco: '', bairro: '', cidade: '', estado: '', descricao: '', destaque: false,
};

export default function Properties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTipo, setFilterTipo] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchProperties = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterTipo) params.tipo = filterTipo;
      if (filterStatus) params.status = filterStatus;
      const { data } = await api.get('/properties', { params });
      setProperties(data);
    } catch {
      toast.error('Erro ao carregar imóveis');
    } finally {
      setLoading(false);
    }
  }, [filterTipo, filterStatus]);

  useEffect(() => { fetchProperties(); }, [fetchProperties]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...EMPTY_FORM, ...p,
      valor: p.valor || '',
      valor_condominio: p.valor_condominio || '',
      area: p.area || '',
      quartos: p.quartos || '',
      banheiros: p.banheiros || '',
      vagas: p.vagas || '',
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        valor: parseFloat(form.valor) || 0,
        valor_condominio: parseFloat(form.valor_condominio) || 0,
        area: parseFloat(form.area) || 0,
        quartos: parseInt(form.quartos) || 0,
        banheiros: parseInt(form.banheiros) || 0,
        vagas: parseInt(form.vagas) || 0,
      };
      if (editing) {
        await api.put(`/properties/${editing.id}`, payload);
        toast.success('Imóvel atualizado!');
      } else {
        await api.post('/properties', payload);
        toast.success('Imóvel cadastrado!');
      }
      setShowModal(false);
      fetchProperties();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar imóvel');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este imóvel?')) return;
    try {
      await api.delete(`/properties/${id}`);
      toast.success('Imóvel excluído');
      setProperties((p) => p.filter((x) => x.id !== id));
    } catch {
      toast.error('Erro ao excluir');
    }
  };

  const filtered = properties.filter((p) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      p.titulo?.toLowerCase().includes(s) ||
      p.bairro?.toLowerCase().includes(s) ||
      p.cidade?.toLowerCase().includes(s)
    );
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content p-6 lg:p-8 pt-16 lg:pt-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Imóveis
            </h1>
            <p className="text-stone-500 text-sm mt-0.5">{properties.length} imóveis cadastrados</p>
          </div>
          <Button
            data-testid="btn-novo-imovel"
            onClick={openNew}
            className="bg-[#064E3B] hover:bg-[#022C22] text-white gap-2"
          >
            <Plus size={16} /> Novo Imóvel
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              placeholder="Buscar por nome, bairro, cidade..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 border-stone-200 text-sm"
            />
          </div>
          <Select value={filterTipo || "all"} onValueChange={(v) => setFilterTipo(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 h-9 border-stone-200 text-sm">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              {TIPOS.filter(Boolean).map((t) => (
                <SelectItem key={t} value={t}>{TIPO_LABELS[t] || t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
            <SelectTrigger className="w-44 h-9 border-stone-200 text-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              {STATUS_LIST.filter(Boolean).map((s) => (
                <SelectItem key={s} value={s}>{STATUS_LABELS[s] || s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-center py-16 text-stone-400">Carregando imóveis...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Building2 size={40} className="mx-auto text-stone-200 mb-3" />
            <p className="text-stone-500 text-sm">Nenhum imóvel encontrado</p>
            <Button onClick={openNew} className="mt-4 bg-[#064E3B] text-white gap-2">
              <Plus size={14} /> Cadastrar imóvel
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((p) => (
              <div
                key={p.id}
                data-testid="property-card"
                className="bg-white border border-stone-200 rounded-lg overflow-hidden hover:-translate-y-0.5 hover:shadow-md transition-all group"
              >
                {/* Image placeholder */}
                <div className="h-36 bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center relative">
                  <Building2 size={32} className="text-stone-300" />
                  {p.destaque && (
                    <span className="absolute top-2 left-2 bg-[#C27803] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      Destaque
                    </span>
                  )}
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => openEdit(p)}
                      className="w-7 h-7 bg-white rounded-md flex items-center justify-center shadow-sm hover:bg-stone-50"
                    >
                      <Edit2 size={12} className="text-stone-600" />
                    </button>
                    <button
                      onClick={() => handleDelete(p.id)}
                      className="w-7 h-7 bg-white rounded-md flex items-center justify-center shadow-sm hover:bg-rose-50"
                    >
                      <Trash2 size={12} className="text-rose-500" />
                    </button>
                  </div>
                </div>
                <div className="p-3.5">
                  <div className="flex items-start justify-between mb-1.5">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${STATUS_COLORS[p.status] || ''}`}>
                      {STATUS_LABELS[p.status] || p.status}
                    </span>
                    <span className="text-[10px] text-stone-400 font-medium">{TIPO_LABELS[p.tipo] || p.tipo}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-stone-800 line-clamp-2 mb-1">{p.titulo}</h3>
                  <div className="flex items-center gap-1 text-stone-400 text-xs mb-2">
                    <MapPin size={11} />
                    <span className="truncate">{p.bairro}{p.cidade ? `, ${p.cidade}` : ''}</span>
                  </div>
                  <p className="text-base font-bold text-stone-900 mb-2">{formatCurrency(p.valor)}</p>
                  {(p.area > 0 || p.quartos > 0) && (
                    <div className="flex items-center gap-3 text-xs text-stone-500">
                      {p.area > 0 && <span>{p.area}m²</span>}
                      {p.quartos > 0 && (
                        <span className="flex items-center gap-1"><BedDouble size={11} />{p.quartos}</span>
                      )}
                      {p.banheiros > 0 && (
                        <span className="flex items-center gap-1"><Bath size={11} />{p.banheiros}</span>
                      )}
                      {p.vagas > 0 && (
                        <span className="flex items-center gap-1"><Car size={11} />{p.vagas}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 sticky top-0 bg-white">
                <h2 className="font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {editing ? 'Editar Imóvel' : 'Novo Imóvel'}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-stone-400 hover:text-stone-600">
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleSave} className="p-6 space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold text-stone-700">Título *</Label>
                  <Input placeholder="Ex: Apartamento 3 Suítes — Beira Mar" value={form.titulo} onChange={set('titulo')} required className="border-stone-200" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-semibold text-stone-700">Tipo</Label>
                    <Select value={form.tipo} onValueChange={(v) => setForm((f) => ({ ...f, tipo: v }))}>
                      <SelectTrigger className="border-stone-200"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TIPOS.filter(Boolean).map((t) => <SelectItem key={t} value={t}>{TIPO_LABELS[t]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-semibold text-stone-700">Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                      <SelectTrigger className="border-stone-200"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_LIST.filter(Boolean).map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-semibold text-stone-700">Valor (R$) *</Label>
                    <Input type="number" placeholder="0" value={form.valor} onChange={set('valor')} required className="border-stone-200" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-semibold text-stone-700">Condomínio (R$)</Label>
                    <Input type="number" placeholder="0" value={form.valor_condominio} onChange={set('valor_condominio')} className="border-stone-200" />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { k: 'area', label: 'Área (m²)' },
                    { k: 'quartos', label: 'Quartos' },
                    { k: 'banheiros', label: 'Banheiros' },
                    { k: 'vagas', label: 'Vagas' },
                  ].map(({ k, label }) => (
                    <div key={k} className="space-y-1.5">
                      <Label className="text-xs font-semibold text-stone-700">{label}</Label>
                      <Input type="number" placeholder="0" value={form[k]} onChange={set(k)} className="border-stone-200" />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-semibold text-stone-700">Endereço</Label>
                    <Input placeholder="Rua, número, apto" value={form.endereco} onChange={set('endereco')} className="border-stone-200" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-semibold text-stone-700">Bairro</Label>
                    <Input placeholder="Bairro" value={form.bairro} onChange={set('bairro')} className="border-stone-200" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2 space-y-1.5">
                    <Label className="text-sm font-semibold text-stone-700">Cidade</Label>
                    <Input placeholder="Florianópolis" value={form.cidade} onChange={set('cidade')} className="border-stone-200" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-semibold text-stone-700">Estado</Label>
                    <Input placeholder="SC" maxLength={2} value={form.estado} onChange={set('estado')} className="border-stone-200" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold text-stone-700">Descrição</Label>
                  <textarea
                    placeholder="Descrição do imóvel..."
                    value={form.descricao}
                    onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#064E3B]/20 focus:border-[#064E3B] resize-none"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.destaque}
                    onChange={(e) => setForm((f) => ({ ...f, destaque: e.target.checked }))}
                    className="accent-[#064E3B]"
                  />
                  Imóvel em destaque
                </label>
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1 border-stone-200">Cancelar</Button>
                  <Button type="submit" disabled={saving} className="flex-1 bg-[#064E3B] hover:bg-[#022C22] text-white">
                    {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : editing ? 'Salvar' : 'Cadastrar'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
